import React from 'react';
import PropTypes from 'prop-types';
import { useSpring, animated, config } from '@react-spring/web';
import { ReactSVG } from 'react-svg';
import { Textfit } from 'react-textfit';
import trinityLogo from '../../assets/trinity_logo.svg';
import background from '../../assets/backgrounds/diagonal_stripes.svg';

import circle from '../../assets/trinty_progress/background.svg';
import front from '../../assets/trinty_progress/front.svg';
import pointer from '../../assets/trinty_progress/pointer.svg';
import text_high from '../../assets/trinty_progress/text_high.svg';
import text_medium from '../../assets/trinty_progress/text_medium.svg';
import text_low from '../../assets/trinty_progress/text_low.svg';


import '../Slides.scss'
import './Trinity.scss';

const TrinityProgressSlide = ({ data }) => {

  const {
    kid_name,
    progress,
    current_trinity_stage,
    next_trinity_stage
  } = data;
  
  const pointerRotation = {
    low: -42,
    medium: 37,
    high: 135,
  }[progress] || 0;

  const progressPercent = {
    low: 0.29,
    medium: 0.50,
    high: 0.85,
  }[progress] || 0;


  const trinityLogoSpring = useSpring({ 
    delay: 400, 
    from: { opacity: 0, transform: 'translateY(20px)' }, 
    to: { opacity: 1, transform: 'translateY(0px)' } 
  });

  const titleSpring = useSpring({
    delay: 400,
    from: { opacity: 0, transform: 'translateY(20px)' },
    to: { opacity: 1, transform: 'translateY(0px)' }
  });

  const subTitleSpring = useSpring({
    delay: 600,
    from: { opacity: 0, transform: 'translateY(20px)' },
    to: { opacity: 1, transform: 'translateY(0px)' }
  });

  const progressSpring = useSpring({
    from: { opacity: 0, rotate: -90, scale: 0.8 },
    to: { opacity: 1, rotate: 0, scale: 1 },
    config: config.wobbly,
    delay: 800
  });

  const pointerSpring = useSpring({
    from: { transform: 'rotate(-180deg)', opacity: 0 },
    to: [
      { transform: `rotate(${pointerRotation + 10}deg)`, opacity: 1 },
      { transform: `rotate(${pointerRotation}deg)` },
    ],
    config: { tension: 140, friction: 12, mass: 2, },
    delay: 1200,
  });

  const circleFillSpring = useSpring({
    from: { progress: 0, rotate: -180, opacity: 0 },
    to: { progress: progressPercent, rotate: -143, opacity: 1 },
    config: { tension: 140, friction: 12, mass: 2 },
    delay: 1200
  });

  const stickerTextSpring = useSpring({
    from: { opacity: 0, transform: 'scale(0.6) rotate(-10deg)' },
    to: { opacity: 1, transform: 'scale(1) rotate(0deg)' },
    config: config.wobbly,
    delay: 1600,
  });


  const nextStageSpring = useSpring({
    from: { opacity: 0, transform: 'scale(0.8)' },
    to: { opacity: 1, transform: 'scale(1)' },
    config: { tension: 200, friction: 20 },
    delay: 600,
  });

  const appear = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 },
    delay: 1000,
  })

  const backgroundScale = useSpring({
    loop: { reverse: true },
    from: { transform: 'scale(1)' },
    to: { transform: 'scale(1.1)' },
    config: { duration: 8000 },
  });

  const backgroundSpring = {
    ...appear,
    ...backgroundScale
  }

  const boxSpring = useSpring({
    delay: 400,
    from: { opacity: 0, transform: 'translateY(100vh)' },
    to: { opacity: 1, transform: 'translateY(0px)' }
  });

  let textSelection = text_low;
  if (progress === 'medium') {
    textSelection = text_medium;
  }
  if (progress === 'high') {
    textSelection = text_high
  }

  return (
    <div className='Slide__trinity_progress'>
      <animated.div className='Slide__trinity_progress__logo' style={trinityLogoSpring}>
        <ReactSVG src={trinityLogo} />
      </animated.div>
      <animated.h1 className='Slide__trinity_progress__title' style={titleSpring}>
        <Textfit className='Slide__trinity_progress__title__kidName' max={20} mode="single" style={{ maxWidth: '140px' }}>
          {`${kid_name} is on:`}
        </Textfit>
        {current_trinity_stage.toLowerCase().includes('grade') ? current_trinity_stage : `${current_trinity_stage} grade` }
      </animated.h1>
      <animated.h2 className='Slide__trinity_progress__explainer' style={subTitleSpring}>
        This is how they are progressing
      </animated.h2>
      <animated.div className='Slide__trinity_progress__sticker' style={progressSpring}>
        <ReactSVG className='Slide__trinity_progress__sticker__circle' src={circle} />
        <animated.div
          className='Slide__trinity_progress__sticker__circleFill'
          style={{
            transform: circleFillSpring.rotate.to(r => `rotate(${r}deg)`),
            background: circleFillSpring.progress.to(p => 
              `conic-gradient(#0183C6 ${p}turn, transparent 0)`)
          }}
        />
        <ReactSVG className='Slide__trinity_progress__sticker__front' src={front} />
        <animated.div className='Slide__trinity_progress__sticker__pointer' style={pointerSpring}>
          <ReactSVG src={pointer} />
        </animated.div>
        <animated.div className='Slide__trinity_progress__sticker__text' style={stickerTextSpring}>
          <ReactSVG src={textSelection} />
        </animated.div>
        <span className="Slide__trinity_progress__sticker__label">
          {current_trinity_stage}
        </span>
      </animated.div>
      <animated.div className='Slide__trinity_progress__box' style={boxSpring}>
        <animated.h2 className='Slide__trinity_progress__box__content' style={nextStageSpring}>
          {next_trinity_stage && `Next stage will be ${next_trinity_stage}`}
        </animated.h2>
      </animated.div>
      <animated.div className='Slide__trinity_progress__background' style={backgroundSpring}>
        <ReactSVG src={background} />
      </animated.div>
    </div>
  )
}

export default TrinityProgressSlide;

const DataProps = {
  current_trinity_stage: PropTypes.string.isRequired,
}

TrinityProgressSlide.propTypes = {
  data: PropTypes.shape(DataProps).isRequired,
}
